import React from 'react';
import LinearGradient from 'react-native-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { avatarDefault } from '@/assets/images';
import { Scroll, Stack } from '@/components/layout';
import { Avatar, Button, showAlert, SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useGuardianPlayersQuery, useGuardianProfileQuery } from '@/features/guardian/queries';
import {
  OnboardingCallout,
  OnboardingInviteCoGuardian,
  PlayerCard,
} from '@/features/onboarding/components';
import { toMatchedPlayer } from '@/features/onboarding/utils/player-card';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { useSessionStore } from '@/stores/session-store';
import { colors, guardianColors } from '@/theme/colors';

const strings = STRINGS.onboarding.finish;

/** Light diagonal gradient shared with the onboarding wizard (matches Figma background). */
const BACKGROUND_GRADIENT = ['#EEF3FF', '#F6F8FC', '#EEF3FF'];
const BACKGROUND_LOCATIONS = [0.08, 0.47, 0.91];

/**
 * Onboarding confirmation ("You're all set!"). Reached once the guardian finishes —
 * or skips through — the last wizard step. Purely presentational: onboarding progress
 * is already persisted by the wizard, so this screen only celebrates completion and
 * routes on to the dashboard via "Take me home".
 */
export function OnboardingFinishScreen() {
  const { scale } = useScaling();
  const insets = useSafeAreaInsets();
  const styles = useStyles();
  const navigation = useGuardianStackNavigation();

  const uid = useSessionStore(s => s.user?.uid);
  const { data: guardianProfile } = useGuardianProfileQuery(uid);
  const { data: guardianPlayers } = useGuardianPlayersQuery(uid);
  const photoUrl = guardianProfile?.photoUrl;

  // With linked players we show the "My Kids" list; otherwise the "Add your child"
  // prompt and an empty "Upcoming" state.
  const players = React.useMemo(
    () => (guardianPlayers ?? []).map(toMatchedPlayer),
    [guardianPlayers],
  );
  const hasPlayers = players.length > 0;

  const goToDashboard = React.useCallback(() => {
    navigation.replace('GuardianDashboard');
  }, [navigation]);

  // Co-guardian invite flow isn't built yet — acknowledge the tap for now.
  const handleInvite = React.useCallback(() => {
    showAlert({
      title: STRINGS.common.noticeTitle,
      message: STRINGS.common.featureUnderProgress,
      buttons: [{ text: STRINGS.errors.ok }],
    });
  }, []);

  const top = insets.top > 0 ? insets.top + scale(16) : scale(50);

  return (
    <LinearGradient
      colors={BACKGROUND_GRADIENT}
      locations={BACKGROUND_LOCATIONS}
      start={{ x: 1, y: 0 }}
      end={{ x: 0, y: 1 }}
      style={styles.background}>
      <Stack flex={1} align="center">
        <Stack flex={1} width={scale({ phone: 344, tablet: 380 })} style={{ paddingTop: top }}>
          <Scroll showsVerticalScrollIndicator={false} contentContainerStyle={styles.bodyContent}>
            {/* Guardian avatar */}
            <Stack align="center">
              {/* <Image
                source={photoUrl ? { uri: photoUrl } : avatarDefault}
                defaultSource={avatarDefault}
                style={styles.avatar}
                resizeMode="cover"
              /> */}
              <Avatar size={scale(120)} borderColor={colors['0']} borderWidth={scale(2)}>
                <Avatar.Image source={photoUrl ? { uri: photoUrl } : avatarDefault} />
              </Avatar>
            </Stack>

            {/* Heading + subtitle */}
            <Text variant="Font Guardian App/Heading/Heading H6 18px" style={styles.title}>
              {strings.title}
            </Text>
            {!hasPlayers && (
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Regular"
                style={styles.subtitle}>
                {strings.subtitle}
              </Text>
            )}

            {hasPlayers ? (
              /* "My Kids" — the golfers linked to this guardian. */
              <>
                <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionHeading}>
                  {strings.myKids}
                </Text>
                <Stack gap={scale(12)}>
                  {players.map(player => (
                    <PlayerCard key={player.id} player={player} />
                  ))}
                </Stack>
              </>
            ) : (
              <>
                {/* "Add your child" card */}
                <OnboardingCallout align="flex-start" style={styles.containerAddYourChild}>
                  <Stack align="center" justify="center" style={styles.avatarRing}>
                    <SvgIcon
                      name="IcUserAdd01"
                      size={scale(20)}
                      color={guardianColors['Main/Main']}
                    />
                  </Stack>
                  <Stack flex={1} gap={scale(2)}>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                      style={styles.addChildTitle}>
                      {strings.addChildTitle}
                    </Text>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.addChildSubTitle}>
                      {strings.addChildSubtitle}
                    </Text>
                  </Stack>
                </OnboardingCallout>
                {/* <Stack
                  direction="row"
                  align="center"
                  gap={scale(12)}
                  marginTop={scale(28)}
                  style={styles.addChildCard}>
                  <Stack align="center" justify="center" style={styles.avatarRing}>
                    <SvgIcon
                      name="IcUserCheck"
                      size={scale(22)}
                      color={guardianColors['Main/Main']}
                    />
                  </Stack>
                  <Stack flex={1} gap={scale(4)}>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                      style={styles.addChildTitle}>
                      {strings.addChildTitle}
                    </Text>
                    <Text
                      variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                      style={styles.mutedText}>
                      {strings.addChildSubtitle}
                    </Text>
                  </Stack>
                </Stack> */}
              </>
            )}
            {/* Upcoming — empty state */}
            <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.sectionHeading}>
              {strings.upcoming}
            </Text>
            <Stack direction="row" align="center" gap={scale(12)} style={styles.emptyCard}>
              <Stack align="center" justify="center" style={styles.emptyIcon}>
                <SvgIcon name="IcCalendar2" size={scale(20)} color={guardianColors['6']} />
              </Stack>
              <Stack flex={1} gap={scale(4)}>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 14 Bold"
                  style={styles.emptyTitle}>
                  {strings.emptyTitle}
                </Text>
                <Text
                  variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                  style={styles.mutedText}>
                  {strings.emptySubtitle}
                </Text>
              </Stack>
              {/* Arrow points up toward the "Add your child" card above. */}
              <SvgIcon name="IcArrowUp" size={scale(20)} color={guardianColors['6']} />
            </Stack>

            {/* Invite a co-guardian */}
            {!!players.length && (
              <Stack marginTop={scale(20)}>
                <OnboardingInviteCoGuardian onInvite={handleInvite} />
              </Stack>
            )}
          </Scroll>

          {/* Footer — "Take me home" */}
          <Stack
            style={{
              paddingTop: scale(12),
              paddingBottom: insets.bottom + (insets.bottom <= 0 ? scale(12) : 0),
            }}>
            <Button onPress={goToDashboard} style={styles.homeButton}>
              <Button.Text
                variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
                style={styles.homeLabel}>
                {strings.takeMeHome}
              </Button.Text>
              <Button.Icon
                name="IcArrowRight"
                position="right"
                size={scale(20)}
                color={colors['0']}
              />
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </LinearGradient>
  );
}

const useStyles = createStyles(({ scale }) => ({
  background: {
    flex: 1,
  },
  containerAddYourChild: {
    marginTop: scale(30),
  },
  bodyContent: {
    paddingTop: scale(20),
    paddingBottom: scale(16),
  },
  avatar: {
    width: scale(120),
    height: scale(120),
    borderRadius: scale(60),
    backgroundColor: colors['1'],
  },
  title: {
    marginTop: scale(20),
    color: colors['10'],
    textAlign: 'center',
  },
  subtitle: {
    marginTop: scale(8),
    color: colors['10'],
    textAlign: 'center',
  },
  addChildCard: {
    padding: scale(16),
    borderRadius: scale(16),
    backgroundColor: guardianColors['2'],
    borderWidth: scale(1),
    borderColor: '#DCE7FB',
  },
  avatarRing: {
    width: scale(48),
    height: scale(48),
    borderRadius: scale(24),
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors['Main/40'],
    backgroundColor: guardianColors['Main/10'],
  },
  addChildTitle: {
    color: colors['10'],
  },
  addChildSubTitle: {
    color: colors['10'],
  },
  mutedText: {
    color: guardianColors['6'],
  },
  sectionHeading: {
    marginTop: scale(24),
    marginBottom: scale(12),
    color: colors['10'],
  },
  emptyCard: {
    padding: scale(16),
    borderRadius: scale(16),
    backgroundColor: guardianColors['1'],
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors['3'],
  },
  emptyIcon: {
    width: scale(48),
    height: scale(48),
    borderRadius: scale(8),
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: guardianColors['3'],
    backgroundColor: guardianColors['2'],
  },
  emptyTitle: {
    color: colors['7'],
  },
  homeButton: {
    width: '100%',
    borderRadius: scale(12),
    backgroundColor: guardianColors['Main/Main'],
  },
  homeLabel: {
    color: colors['0'],
  },
}));

export default OnboardingFinishScreen;
